import {
  FETCH_ESTIMATE,
  FETCH_ESTIMATE_SUCCESS,
  FETCH_ESTIMATE_FAILED,
  CLEAR_ESTIMATE
} from "../store/types";
import Polyline from '@mapbox/polyline';
import { firebase } from '../config/configureFirebase';
import { FareCalculator } from '../other/FareCalculator';
import { get } from "firebase/database";

// Why an estimate failed. FETCH_ESTIMATE_FAILED keeps its English sentence as
// the payload, byte for byte (the manual-booking screens and the portal show
// that text as it is); estimateErrorInfo() turns the sentence back into a code
// so a screen can say it in the customer's own language (GoogleMapScreen):
//   estimateErrorInfo(msg) -> { code: 'too_close', distance: '0.5 km' }
//                           | { code: 'no_route' } | { code: 'other' }
export const ESTIMATE_NO_ROUTE = "No Route Found";
const TOO_CLOSE_START = 'Drop location is too close to pickup.';
const tooCloseMessage = (minDistance, unit) =>
  TOO_CLOSE_START + ' Please choose a destination at least ' + minDistance + ' ' + unit + ' away.';

export const estimateErrorInfo = (msg) => {
  if (msg === ESTIMATE_NO_ROUTE) return { code: 'no_route' };
  if (typeof msg === 'string' && msg.indexOf(TOO_CLOSE_START) === 0) {
    const m = /at least (\S+) (km|mi) away/.exec(msg);
    return { code: 'too_close', distance: m ? m[1] + ' ' + m[2] : '' };
  }
  return { code: 'other' };
};

// `opts.settings`: the settings in hand, when the same route is priced again
// for another car type (no read: nothing here then leaves the phone).
export const getEstimate = (bookingData, opts) => async (dispatch) => {
  const   {
      settingsRef
  } = firebase;

  dispatch({
    type: FETCH_ESTIMATE,
    payload: bookingData,
  });


  let res = bookingData.routeDetails;

  if(res){
    let points = Polyline.decode(res.polylinePoints);

    let waypoints = points.map((point) => {
        return {
            latitude: point[0],
            longitude: point[1]
        }
    });

    let settings = opts && opts.settings;
    if (!settings) {
      const settingdata = await get(settingsRef);
      settings = settingdata.val();
    }
    let distance = settings.convert_to_mile? (res.distance_in_km / 1.609344) : res.distance_in_km;

    // Defense-in-depth: coerce non-finite values to 0 BEFORE they reach the
    // booking payload. The Directions API can return null / undefined /
    // negative-zero distances when pickup and drop resolve to essentially
    // the same coordinates (snap-to-road glitches, very short trips).
    // Without this, `parseFloat(NaN).toFixed()` produces "NaN" which then
    // re-parses as NaN, gets written into estimateDistance, and Firebase
    // RTDB rejects the eventual booking push with "Cannot store NaN" —
    // surfacing as a stuck loading spinner on the booking screen.
    //
    // IMPORTANT: parse FIRST, then check. The cloud function /googleapi
    // wrapper sometimes returns time_in_secs (and occasionally
    // distance_in_km) as a stringified number — Number.isFinite is
    // strict and returns false for strings, so the naive
    // `Number.isFinite(res.time_in_secs)` check coerces "300" to 0
    // and the modal then shows "0 mins" for every trip.
    const parsedDistance = parseFloat(distance);
    const parsedTimeSecs = parseFloat(res.time_in_secs);
    const safeDistance = Number.isFinite(parsedDistance) ? parsedDistance : 0;
    const safeTimeSecs = Number.isFinite(parsedTimeSecs) ? parsedTimeSecs : 0;

    // User-facing guard: reject trips below the admin-configured minimum
    // distance (settings.minimumTripDistance, default 0.5 km / mi). This
    // catches the common "drop is the same as pickup" case with a clear
    // error message instead of letting the booking proceed with a 0 km
    // ride at a ~base-fare-only price.
    const minDistance = parseFloat(settings.minimumTripDistance) || 0;
    if (minDistance > 0 && safeDistance < minDistance) {
      dispatch({
        type: FETCH_ESTIMATE_FAILED,
        payload: tooCloseMessage(minDistance, settings.convert_to_mile ? 'mi' : 'km'),
      });
      return;
    }

    let {totalCost, grandTotal, convenience_fees} = FareCalculator(bookingData, safeDistance, safeTimeSecs, bookingData.carDetails, bookingData.instructionData, settings.decimal);

      dispatch({
        type: FETCH_ESTIMATE_SUCCESS,
        payload: {
          pickup:bookingData.pickup,
          drop:bookingData.drop,
          carDetails:bookingData.carDetails,
          instructionData: bookingData.instructionData,
          estimateDistance: parseFloat(parseFloat(safeDistance).toFixed(settings.decimal)),
          fareCost: totalCost ? parseFloat(parseFloat(totalCost).toFixed(settings.decimal)) : 0,
          estimateFare: grandTotal ? parseFloat(parseFloat(grandTotal).toFixed(settings.decimal)) : 0,
          estimateTime: safeTimeSecs,
          convenience_fees: convenience_fees ? parseFloat(parseFloat(convenience_fees).toFixed(settings.decimal)) : 0,
          waypoints: waypoints
        },
      });
  }else{
    dispatch({
      type: FETCH_ESTIMATE_FAILED,
      payload: ESTIMATE_NO_ROUTE,
    });
  }

}

export const clearEstimate = () => (dispatch) => {
    dispatch({
        type: CLEAR_ESTIMATE,
        payload: null,
    });
}
